import { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";

const ACTION_COLORS = {
  CREATE:          "badge-success",
  UPDATE:          "badge-warning",
  DELETE:          "badge-danger",
  LOGIN:           "badge-info",
  LOGIN_FAILED:    "badge-danger",
  APPROVE:         "badge-success",
  REJECT:          "badge-warning",
  RETURN:          "badge-info",
  PASSWORD_CHANGE: "badge-warning",
  ACCESS_DENIED:   "badge-danger",
  EXPORT:          "badge-info",
};

const actionBadge = (action) =>
  "badge " +
  (ACTION_COLORS[action] || "bg-black/5 text-strong border border-white/40");

const ACTION_OPTIONS = [
  "ALL",
  "CREATE",
  "UPDATE",
  "DELETE",
  "LOGIN",
  "LOGIN_FAILED",
  "APPROVE",
  "REJECT",
  "RETURN",
  "PASSWORD_CHANGE",
  "ACCESS_DENIED",
  "EXPORT",
];

const TARGET_OPTIONS = [
  "ALL",
  "Item",
  "Category",
  "Location",
  "User",
  "Transaction",
  "BorrowBatch",
  "PanelTemplate",
  "PanelAssembly",
  "LoanTransaction",
  "InstallationReport",
];

function DetailRow({ log }) {
  const [open, setOpen] = useState(false);
  const hasExtra =
    log.extra_data && Object.keys(log.extra_data).length > 0;
  const hasDetail = log.detail && log.detail.trim();
  const hasHash = log.row_hash && log.row_hash.trim();

  if (!hasExtra && !hasDetail && !hasHash) return null;

  return (
    <tr className="border-b border-dashed">
      <td colSpan={6} className="px-4 pb-2 pt-0">
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-brand-500 hover:underline"
        >
          {open ? "▲ sembunyikan detail" : "▼ lihat detail"}
        </button>
        {open && (
          <div className="mt-1 text-xs bg-black/5 dark:bg-white/5 rounded p-2 font-mono whitespace-pre-wrap break-all">
            {hasDetail && <div className="mb-1 text-muted">{log.detail}</div>}
            {hasExtra && JSON.stringify(log.extra_data, null, 2)}
            {hasHash && (
              <div className="mt-1 pt-1 border-t border-dashed border-white/20 text-muted">
                hash: {log.row_hash}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 30;
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("ALL");
  const [targetFilter, setTargetFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => { setPage(1); }, [search, actionFilter, targetFilter, dateFrom, dateTo]);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError("");

    const token = localStorage.getItem("accessToken");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const params = new URLSearchParams();
    params.set("page", page);
    params.set("page_size", pageSize);
    if (search.trim()) params.set("search", search.trim());
    if (actionFilter !== "ALL") params.set("action", actionFilter);
    if (targetFilter !== "ALL") params.set("target_type", targetFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    fetch(`${API_BASE_URL}/api/audit/?${params.toString()}`, { headers })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 403)
            throw new Error("Hanya superadmin yang bisa melihat audit log.");
          const data = await res.json().catch(() => null);
          throw new Error(data?.detail || "Gagal memuat audit log.");
        }
        return res.json();
      })
      .then((data) => {
        const results = Array.isArray(data) ? data : data.results || [];
        setLogs(results);
        setTotalCount(
          !Array.isArray(data) && typeof data?.count === "number"
            ? data.count
            : results.length
        );
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Gagal memuat audit log.");
        setLoading(false);
      });
  }, [page, search, actionFilter, targetFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const fmt = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-SG", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      : "-";

  const handleExportCSV = () => {
    const token = localStorage.getItem("accessToken");
    const headers = { Authorization: `Bearer ${token}` };
    const params = new URLSearchParams();
    params.set("page_size", 10000);
    // Marks this specific fetch as a bulk export so the backend records it
    // to the audit trail too — "who watches the watchers". Plain paginated
    // viewing (fetchLogs above) intentionally does NOT set this.
    params.set("export", "1");
    if (search.trim()) params.set("search", search.trim());
    if (actionFilter !== "ALL") params.set("action", actionFilter);
    if (targetFilter !== "ALL") params.set("target_type", targetFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    fetch(`${API_BASE_URL}/api/audit/?${params.toString()}`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : data.results || [];
        const cols = ["id", "created_at", "user", "action", "target_type", "target_repr", "detail", "user_agent"];
        const header = cols.join(",");
        const lines = rows.map((r) =>
          cols
            .map((c) => {
              const v = c === "user" ? (r[c] || "-") : (r[c] ?? "");
              return `"${String(v).replace(/"/g, '""')}"`;
            })
            .join(",")
        );
        const csv = [header, ...lines].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const handleVerifyIntegrity = () => {
    setVerifying(true);
    setVerifyResult(null);
    const token = localStorage.getItem("accessToken");
    fetch(`${API_BASE_URL}/api/audit/verify/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.detail || "Gagal menjalankan verifikasi.");
        return data;
      })
      .then((data) => setVerifyResult(data))
      .catch((err) => setVerifyResult({ ok: false, reason: err.message }))
      .finally(() => setVerifying(false));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold mb-2">Audit Log</h1>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(ACTION_COLORS).map(([action, cls]) => (
              <span key={action} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
                {action}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center sm:shrink-0">
          <span className="text-xs text-muted">{totalCount} entri</span>
          <button
            onClick={handleVerifyIntegrity}
            disabled={verifying}
            title="Hitung ulang hash chain dari awal dan pastikan tidak ada entri yang diubah/dihapus di luar aplikasi"
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium hover:bg-white/20 disabled:opacity-50"
          >
            {verifying ? "Memverifikasi…" : "Verifikasi Integritas"}
          </button>
          <button
            onClick={handleExportCSV}
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium hover:bg-white/20"
          >
            Export CSV
          </button>
          <button
            onClick={fetchLogs}
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium hover:bg-white/20"
          >
            Refresh
          </button>
        </div>
      </div>

      {verifyResult && (
        <div
          className={
            "mb-4 text-sm rounded-md px-3 py-2 border flex items-start justify-between gap-3 " +
            (verifyResult.ok
              ? "text-green-700 bg-green-50 border-green-200"
              : "text-red-700 bg-red-50 border-red-200")
          }
        >
          <div>
            {verifyResult.ok ? (
              <>✅ Rantai audit utuh — <b>{verifyResult.checked}</b> entri terverifikasi, tidak ada indikasi perubahan di luar aplikasi.</>
            ) : (
              <>⚠️ Terdeteksi indikasi tamper. {verifyResult.reason || "Rantai hash terputus."}</>
            )}
          </div>
          <button
            onClick={() => setVerifyResult(null)}
            className="text-xs opacity-60 hover:opacity-100 shrink-0"
          >
            ✕
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="w-full sm:max-w-xs">
          <label className="block text-xs font-medium text-muted mb-1">Search</label>
          <input
            type="text"
            placeholder="user, object, detail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm bg-white/80 dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Action</label>
          <CustomSelect
            value={actionFilter}
            onChange={setActionFilter}
            options={ACTION_OPTIONS.map((a) => ({ value: a, label: a === "ALL" ? "Semua Action" : a }))}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Target Type</label>
          <CustomSelect
            value={targetFilter}
            onChange={setTargetFilter}
            options={TARGET_OPTIONS.map((t) => ({ value: t, label: t === "ALL" ? "Semua Target" : t }))}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Dari Tanggal</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm bg-white/80 dark:bg-white/5"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Sampai Tanggal</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm bg-white/80 dark:bg-white/5"
          />
        </div>

        {(search || actionFilter !== "ALL" || targetFilter !== "ALL" || dateFrom || dateTo) && (
          <button
            onClick={() => { setSearch(""); setActionFilter("ALL"); setTargetFilter("ALL"); setDateFrom(""); setDateTo(""); }}
            className="self-end rounded-md border border-red-200 text-red-600 px-3 py-2 text-xs font-medium hover:bg-red-50"
          >
            Reset Filter
          </button>
        )}
      </div>

      {/* Table */}
      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table min-w-[900px]">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left whitespace-nowrap">Waktu</th>
              <th className="px-4 py-2 text-left whitespace-nowrap">User</th>
              <th className="px-4 py-2 text-left whitespace-nowrap">Action</th>
              <th className="px-4 py-2 text-left whitespace-nowrap">Target</th>
              <th className="px-4 py-2 text-left whitespace-nowrap">Object</th>
              <th className="px-4 py-2 text-left whitespace-nowrap">Browser / UA</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted">
                  Memuat audit log…
                </td>
              </tr>
            )}

            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted">
                  Tidak ada entri audit ditemukan.
                </td>
              </tr>
            )}

            {!loading &&
              logs.map((log) => (
                <>
                  <tr key={log.id} className="border-t">
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-muted">
                      {fmt(log.created_at)}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium whitespace-nowrap">
                      {log.user || "-"}
                    </td>
                    <td className="px-4 py-2">
                      <span className={actionBadge(log.action)}>{log.action}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted whitespace-nowrap">
                      {log.target_type || "-"}
                    </td>
                    <td className="px-4 py-2 text-sm max-w-[200px] truncate" title={log.target_repr}>
                      {log.target_repr || "-"}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted max-w-[180px] truncate" title={log.user_agent}>
                      {log.user_agent
                        ? log.user_agent.split(" ").slice(0, 3).join(" ")
                        : "-"}
                    </td>
                  </tr>
                  <DetailRow key={`detail-${log.id}`} log={log} />
                </>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            First
          </button>
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>
        </div>
        <span className="text-xs text-muted">
          Halaman {page} dari {totalPages} ({totalCount} total)
        </span>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
          <button
            className="rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-xs font-medium disabled:opacity-30"
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          >
            Last
          </button>
        </div>
      </div>

    </div>
  );
}

export default Audit;
